import {
  faBell,
  faFolders,
  faQuestionCircle,
  faRectanglePro,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import { faDrawCircle, faHome } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Switch, useRouteMatch } from 'react-router-dom';

import {
  Button,
  Icon,
  ListItem,
  Logo,
  MenuDivider,
  Nav,
  NavItem,
  Popover,
  SettingsIcon,
} from '@feathr/components';
import AccountSwitcher from '@feathr/extender/components/AccountSwitcher/AccountSwitcher';
import UnreadNotifications from '@feathr/extender/components/UnreadNotifications';
import { useAccount, useLocalUrl, useUser } from '@feathr/extender/state';

import DataNav from './DataNav';
import EventNav from './EventNav';
import HelpNav from './HelpNav';
import HomeNav from './HomeNav';
import SettingsNav from './SettingsNav';
import UserNav from './UserNav';

import styles from './PrimaryNav.css';

import noImg from '@feathr/extender/images/no-img.png';

interface IProps {
  className?: string;
  secondaryNav?: React.ReactNode;
}

function PrimaryNav({ className }: IProps): JSX.Element | null {
  const localUrl = useLocalUrl();
  const match = useRouteMatch<{ accountId: string; primary: string }>({
    path: '/:accountId/:primary',
  });
  const account = useAccount();
  const user = useUser();
  const { t } = useTranslation();

  const picture = (!!user && user.get('picture')) || '';
  const activePackage = account?.activePackage;

  const isPro = account.isPro;

  if (!match) {
    return null;
  }

  return (
    <Nav
      className={classNames(styles.root, className)}
      label={'Primary navigation'}
      type={'vertical'}
    >
      <ListItem className={styles.logo}>
        <AccountSwitcher />
      </ListItem>
      {(activePackage || user?.isSudoer) && (
        <>
          <NavItem
            className={classNames({ [styles.active]: match.params.primary === 'marketing' })}
            icon={faHome}
            rel={'home'}
            to={localUrl('marketing')}
          >
            {t('Home')}
          </NavItem>
          <NavItem
            className={classNames({ [styles.active]: match.params.primary === 'projects' })}
            icon={faFolders}
            to={localUrl('/projects')}
          >
            {t('Projects')}
          </NavItem>
          <NavItem
            className={classNames({ [styles.active]: match.params.primary === 'data' })}
            icon={faDrawCircle}
            to={localUrl('/data')}
          >
            {t('Community')}
          </NavItem>
        </>
      )}
      <MenuDivider />
      <Switch>
        <Route path={'/:accountId/accounts'}>
          <NavItem
            className={classNames({ [styles.active]: match.params.primary === 'accounts' })}
            icon={faUsers}
            to={localUrl('/accounts')}
          >
            {t('Accounts')}
          </NavItem>
        </Route>
      </Switch>
      <NavItem className={styles.help}>
        <Popover align={'end'} className={styles.popper} position={'right'} toggle={'onClick'}>
          <Button type={'naked'}>
            <FontAwesomeIcon icon={faQuestionCircle} /> {t('Help')}
          </Button>
          <HelpNav />
        </Popover>
      </NavItem>
      {isPro && (
        <NavItem
          icon={<FontAwesomeIcon icon={faRectanglePro} />}
          to={localUrl('/settings/pro-resources')}
        >
          {t('Resources')}
        </NavItem>
      )}
      {match.params.primary === 'notifications' && (
        <NavItem
          className={classNames({ [styles.active]: match.params.primary === 'notifications' })}
          icon={<FontAwesomeIcon icon={faBell} />}
          to={localUrl('/notifications')}
        >
          {t('Notifications')}
        </NavItem>
      )}
      {match.params.primary === 'settings' && (
        <NavItem
          className={classNames({ [styles.active]: match.params.primary === 'settings' })}
          icon={<Icon icon={SettingsIcon} />}
          to={localUrl('/settings')}
        >
          {t('Settings')}
        </NavItem>
      )}
      <NavItem className={styles.notifications}>
        <UnreadNotifications />
      </NavItem>
      <NavItem>
        <Popover align={'end'} className={styles.popper} position={'right'} toggle={'onClick'}>
          <div className={styles.avatar}>
            <img alt={''} src={picture || noImg} /> {t('Profile & Settings')}
          </div>
          <UserNav />
        </Popover>
      </NavItem>

      <NavItem className={styles.secondary}>
        {activePackage || user?.isSudoer ? (
          <Switch>
            <Route path={'/:accountId/marketing'}>
              <HomeNav />
            </Route>
            <Route path={'/:accountId/data'}>
              <DataNav />
            </Route>
            {/* Don't display secondary navigation for this path */}
            <Route path={'/:accountId/projects/add'} />
            {/* Add optional :tertiary param to allow identifying subsection */}
            <Route path={'/:accountId/projects/:eventId/:tertiary?'}>
              <EventNav />
            </Route>
            <Route path={'/:accountId/settings'}>
              <SettingsNav />
            </Route>
          </Switch>
        ) : (
          <Switch>
            <Route path={'/:accountId/settings'}>
              <SettingsNav />
            </Route>
          </Switch>
        )}
      </NavItem>
      <div className={styles.powered}>
        {t('Powered by')} <Logo />
      </div>
    </Nav>
  );
}

export default observer(PrimaryNav);
